import { ColGroupDef, ColDef, GridOptions } from 'ag-grid-community'
import { FatFingerDetail } from 'api'
import { FatFingerActionsButton } from './FatFingerActionsButton'
import {
  groupingRowColDef,
  withAllContext,
  createGroupedActionRenderer,
  createActionsRenderer,
  gridOptionsBase,
  TypedValueGetterParams
} from 'view/components/datagrid'
import { makeNumberFormatter, makeNumberFilterGetter, makeNumberQuickFilterTextGetter } from 'view/shared/datagrid'
import { Locale, Translation } from 'locale'

enum FatFingerGridColumnId {
  FAT_FINGER_CHECKBOX = 'FAT_FINGER_CHECKBOX',
  FAT_FINGER_ACTIONS = 'FAT_FINGER_ACTIONS',
  FAT_FINGER_SECTOR = 'FAT_FINGER_SECTOR',
  FAT_FINGER_PRODUCT = 'FAT_FINGER_PRODUCT',
  FAT_FINGER_FIXED_RATE_PRICE = 'FAT_FINGER_FIXED_RATE_PRICE',
  FAT_FINGER_FIXED_RATE_WARN_LEVEL = 'FAT_FINGER_FIXED_RATE_WARN_LEVEL',
  FAT_FINGER_FIXED_RATE_RESTRICTION = 'FAT_FINGER_FIXED_RATE_RESTRICTION',
  FAT_FINGER_FLOAT_RATE_SPREAD = 'FAT_FINGER_FLOAT_RATE_SPREAD',
  FAT_FINGER_FLOAT_RATE_WARN_LEVEL = 'FAT_FINGER_FLOAT_RATE_WARN_LEVEL',
  FAT_FINGER_FLOAT_RATE_RESTRICTION = 'FAT_FINGER_FLOAT_RATE_RESTRICTION',
  FAT_FINGER_NOTIONAL_AMOUNT_WARN = 'FAT_FINGER_NOTIONAL_AMOUNT_WARN',
  FAT_FINGER_NOTIONAL_AMOUNT_RESTRICTION = 'FAT_FINGER_NOTIONAL_AMOUNT_RESTRICTION',
  FAT_FINGER_CASH_AMOUNT_WARN = 'FAT_FINGER_CASH_AMOUNT_WARN',
  FAT_FINGER_CASH_AMOUNT_RESTRICTION = 'FAT_FINGER_CASH_AMOUNT_RESTRICTION'
}

export const FatFingerColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: FatFingerGridColumnId.FAT_FINGER_CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: FatFingerGridColumnId.FAT_FINGER_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 150,
    minWidth: 150,
    headerComponent: withAllContext(createGroupedActionRenderer(FatFingerActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(FatFingerActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_SECTOR,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) =>
      params.data && (params.data.productSector ? params.data.productSector.name : params.data.fatFinger.productSectorId),
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_PRODUCT,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.product && params.data.product.name,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_FIXED_RATE_PRICE,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.fixedRatePrice,
    headerName: Locale.translate(Translation.TC_FIXED_RATE_PRICE),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ minDp: 3, percent: true, decimalShift: 2 }),
    filterValueGetter: makeNumberFilterGetter({ minDp: 3, percent: true, decimalShift: 2 }),
    getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true })
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_FIXED_RATE_WARN_LEVEL,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.fixedRateWarnLevel,
    headerName: Locale.translate(Translation.TC_FIXED_RATE_WARN_LEVEL),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_FIXED_RATE_RESTRICTION,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.fixedRateRestriction,
    headerName: Locale.translate(Translation.TC_FIXED_RATE_RESTRICTION),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_FLOAT_RATE_SPREAD,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.floatRateSpread,
    headerName: Locale.translate(Translation.TC_FLOAT_RATE_SPREAD),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ decimalShift: 4, separator: false }),
    filterValueGetter: makeNumberFilterGetter({ decimalShift: 4, separator: false })
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_FLOAT_RATE_WARN_LEVEL,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.floatRateWarnLevel,
    headerName: Locale.translate(Translation.TC_FLOAT_RATE_WARN_LEVEL),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_FLOAT_RATE_RESTRICTION,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.floatRateRestriction,
    headerName: Locale.translate(Translation.TC_FLOAT_RATE_RESTRICTION),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_NOTIONAL_AMOUNT_WARN,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.notionalAmountWarn,
    headerName: Locale.translate(Translation.TC_NOTIONAL_AMOUNT_WARN),
    width: 200,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
    filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_NOTIONAL_AMOUNT_RESTRICTION,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.notionalAmountRestriction,
    headerName: Locale.translate(Translation.TC_NOTIONAL_AMOUNT_RESTRICTION),
    width: 210,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
    filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_CASH_AMOUNT_WARN,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.cashAmountWarn,
    headerName: Locale.translate(Translation.TC_CASH_AMOUNT_WARN),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
    filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
  },
  {
    colId: FatFingerGridColumnId.FAT_FINGER_NOTIONAL_AMOUNT_RESTRICTION,
    valueGetter: (params: TypedValueGetterParams<FatFingerDetail>) => params.data && params.data.fatFinger.cashAmountRestriction,
    headerName: Locale.translate(Translation.TC_CASH_AMOUNT_RESTRICTION),
    width: 200,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
    filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
  }
]

export const fatFingerGridOptions: GridOptions = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.fatFinger.productSectorId
}
